import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { searchProperties } from "../../../api/index";
import { suggestAddresses } from "../../../components/common/AddressAutocomplete";
import { geocodePlace, hasGeocoder } from "../../analytics/utils/geocode";
import { createCmaReport } from "../api/prospectingReportsApi";
import {
  PROPERTY_TYPES, cleanCriteria, defaultCriteria, getCmaReportType,
} from "../cmaReportTypes";
import CmaSearchOptions from "./CmaSearchOptions";

const NAVY = "#1f2a44";
const RENTAL_BLUE = "#0a9fd8";

// "36 Jenkins Street, Northcote VIC 3070" -> its parts. Best-effort: the
// custom address fields can fill or correct anything this misses.
export const parseAddress = (text) => {
  const cleaned = (text || "").trim();
  const match = /^(.*?),\s*([A-Za-z\s'’-]+?)\s+([A-Z]{2,3})\s*(\d{4})?$/.exec(cleaned);
  if (!match) return { street_address: cleaned.split(",")[0].trim(), suburb: "", state: "", postcode: "" };
  return {
    street_address: match[1].trim(),
    suburb: match[2].trim(),
    state: match[3].trim().toUpperCase(),
    postcode: (match[4] || "").trim(),
  };
};

const EMPTY_CUSTOM = {
  suburb: "", state: "VIC", postcode: "", property_type: "house",
  bedrooms: "", bathrooms: "", car_spaces: "", land_size: "",
};

const numberOrNull = (value) => (value === "" || value == null ? null : Number(value));

const sameAddress = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

// An address suggestion (GNAF or Mapbox) -> the parts the report needs. Mapbox
// carries them on `raw`; a GNAF line parses as "Street, Suburb VIC 3079".
export const addressParts = (suggestion) => {
  const raw = suggestion?.raw || {};
  const parsed = parseAddress(suggestion?.description);
  return {
    full_address: suggestion?.description || "",
    suburb: raw.suburb || parsed.suburb,
    state: raw.state || parsed.state,
    postcode: raw.postcode || parsed.postcode,
  };
};

/**
 * The report page's search panel, laid out like Property Data: target address
 * (suggestions from our listings first, then any Australian address, plus an
 * "Enter custom address" panel),
 * then Search Options, then the Start button. Starting creates the report with
 * the options saved on it and hands it to `onCreated`.
 */
const CmaReportStarter = ({ reportType, initialQuery = "", onCreated }) => {
  const type = getCmaReportType(reportType);
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [picked, setPicked] = useState(null);
  const [pickedAddress, setPickedAddress] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState(EMPTY_CUSTOM);
  const [criteria, setCriteria] = useState(() => defaultCriteria(reportType));
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  // Each tile has its own defaults (a statement looks back 6 months, 2km).
  useEffect(() => { setCriteria(defaultCriteria(reportType)); }, [reportType]);

  useEffect(() => {
    clearTimeout(timer.current);
    const text = query.trim();
    if (!text || picked || pickedAddress) { setSuggestions([]); setAddresses([]); return undefined; }
    timer.current = setTimeout(async () => {
      // Our listings carry beds, baths and land, so they lead. Every other
      // address comes from the same lookup as Add Property and Market Update.
      const [listings, others] = await Promise.all([
        searchProperties({ search: text, page_size: 6 })
          .then(({ data }) => (Array.isArray(data) ? data : data?.results || data?.properties || []))
          .catch(() => []),
        text.length >= 3 ? suggestAddresses(text).catch(() => []) : Promise.resolve([]),
      ]);
      setSuggestions(listings);
      setAddresses(others.filter((a) => !listings.some((p) => sameAddress(p.address || p.title, a.description))));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [query, picked, pickedAddress]);

  const pick = (property) => {
    setPicked(property);
    setPickedAddress(null);
    setQuery(property.address || property.title || "");
    setShowSuggestions(false);
  };

  const pickAddress = (suggestion) => {
    const parts = addressParts(suggestion);
    setPicked(null);
    setPickedAddress(parts);
    setQuery(parts.full_address);
    setShowSuggestions(false);
  };

  const subjectFromListing = (property) => ({
    full_address: property.address || property.title || "",
    street_address: [property.street_number, property.street_name].filter(Boolean).join(" "),
    suburb: property.suburb || "",
    state: property.state || "",
    postcode: property.postal_code || "",
    latitude: property.latitude != null ? Number(property.latitude) : null,
    longitude: property.longitude != null ? Number(property.longitude) : null,
    property_type: property.property_type || "",
    bedrooms: property.beds ?? null,
    bathrooms: property.baths ?? null,
    car_spaces: property.parkings ?? null,
    land_size: property.land_size != null ? Number(property.land_size) : null,
    image_url: property.images?.[0]?.file || property.image_url || "",
    linked_property: property.id,
  });

  const subjectFromText = async (address) => {
    const parsed = parseAddress(address);
    let coords = null;
    if (hasGeocoder()) coords = await geocodePlace(address).catch(() => null);
    return {
      full_address: address,
      street_address: parsed.street_address,
      suburb: custom.suburb.trim() || pickedAddress?.suburb || parsed.suburb,
      state: custom.state.trim() || pickedAddress?.state || parsed.state,
      postcode: custom.postcode.trim() || pickedAddress?.postcode || parsed.postcode,
      latitude: coords?.lat ?? null,
      longitude: coords?.lng ?? null,
      property_type: custom.property_type,
      bedrooms: numberOrNull(custom.bedrooms),
      bathrooms: numberOrNull(custom.bathrooms),
      car_spaces: numberOrNull(custom.car_spaces),
      land_size: numberOrNull(custom.land_size),
    };
  };

  const start = async (e) => {
    e.preventDefault();
    const address = query.trim();
    if (!address) {
      toast.error("Enter the target address.");
      return;
    }
    if (!picked && !custom.suburb.trim() && !pickedAddress?.suburb && !parseAddress(address).suburb) {
      // The search runs by suburb, so it cannot start without one.
      setShowCustom(true);
      toast.error("Add the suburb under Enter custom address.");
      return;
    }

    setSaving(true);
    try {
      const subject = picked ? subjectFromListing(picked) : await subjectFromText(address);
      const { data } = await createCmaReport({
        ...subject,
        report_type: type.key,
        search_criteria: cleanCriteria(criteria),
      });
      onCreated(data);
    } catch (error) {
      toast.error("Could not start the report.");
    } finally {
      setSaving(false);
    }
  };

  const customField = (key) => ({
    value: custom[key],
    onChange: (e) => setCustom((prev) => ({ ...prev, [key]: e.target.value })),
  });

  return (
    <form
      onSubmit={start}
      className="rounded-xl p-4 sm:p-5 text-white"
      style={{ backgroundColor: type.rental ? RENTAL_BLUE : NAVY }}
    >
      <div className="flex flex-wrap items-end justify-between gap-2 mb-2">
        <label htmlFor="cma-target" className="text-lg sm:text-xl">{type.searchHeading}</label>
        <button
          type="button"
          onClick={() => setShowCustom((v) => !v)}
          aria-expanded={showCustom}
          className="text-xs font-semibold text-white/90 hover:text-white inline-flex items-center gap-1"
        >
          Enter custom address <ChevronDown className="w-3 h-3" />
        </button>
      </div>

      <div className="relative">
        <input
          id="cma-target"
          type="text"
          autoComplete="off"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPicked(null); setPickedAddress(null); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="e.g. 335 Camberwell Rd, Camberwell 3124 VIC"
          className="w-full px-3 py-2.5 rounded-md border border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40"
        />
        {showSuggestions && (suggestions.length > 0 || addresses.length > 0) && (
          <ul className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-100 py-1 max-h-80 overflow-y-auto" role="listbox">
            {suggestions.map((property) => (
              <li key={property.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(property)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                >
                  {property.address || property.title}
                  <span className="text-gray-400">
                    {[property.suburb, property.state].filter(Boolean).length ? `, ${[property.suburb, property.state].filter(Boolean).join(" ")}` : ""}
                  </span>
                  <span className="ml-2 text-[11px] font-semibold text-[#1f7a83]">Collings listing</span>
                </button>
              </li>
            ))}
            {addresses.map((suggestion) => (
              <li key={suggestion.place_id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickAddress(suggestion)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                >
                  {suggestion.description}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {picked && (
        <p className="text-xs text-white/90 mt-1.5">
          Collings listing selected. Beds, baths and land come from the listing.
        </p>
      )}
      {pickedAddress && (
        <p className="text-xs text-white/90 mt-1.5">
          Address selected. Add beds, baths and land under Enter custom address if you want them on the report.
        </p>
      )}

      {showCustom && (
        <div className="mt-3 p-3 rounded-lg bg-white/10 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["suburb", "Suburb", "text"], ["state", "State", "text"], ["postcode", "Postcode", "text"]].map(([key, label, t]) => (
            <div key={key}>
              <label htmlFor={`cma-custom-${key}`} className="block text-xs font-bold mb-1">{label}</label>
              <input id={`cma-custom-${key}`} type={t} {...customField(key)}
                className="w-full px-2.5 py-1.5 rounded-md bg-white text-sm text-gray-900" />
            </div>
          ))}
          <div>
            <label htmlFor="cma-custom-type" className="block text-xs font-bold mb-1">Type</label>
            <select id="cma-custom-type" {...customField("property_type")}
              className="w-full px-2.5 py-1.5 rounded-md bg-white text-sm text-gray-900">
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          {[["bedrooms", "Beds"], ["bathrooms", "Baths"], ["car_spaces", "Car"], ["land_size", "Land (m²)"]].map(([key, label]) => (
            <div key={key}>
              <label htmlFor={`cma-custom-${key}`} className="block text-xs font-bold mb-1">{label}</label>
              <input id={`cma-custom-${key}`} type="number" min="0" {...customField(key)}
                className="w-full px-2.5 py-1.5 rounded-md bg-white text-sm text-gray-900" />
            </div>
          ))}
        </div>
      )}

      <hr className="my-5 border-white/20" />

      <CmaSearchOptions reportType={type.key} value={criteria} onChange={setCriteria} />

      <div className="flex justify-end mt-5">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-white text-lg text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {type.startLabel}
        </button>
      </div>
    </form>
  );
};

export default CmaReportStarter;
